import { makeAutoObservable, runInAction } from 'mobx';
import type { AdminPayment, PaymentRequestsResponse } from '../types';
import { adminPaymentApi } from '../api/admin_payment_api';

export class AdminPaymentStore {
  // Payment storage
  payments: Map<string, AdminPayment> = new Map();
  paymentsLoading = false;
  paymentsCursor: string | null = null;
  paymentsHasMore = true;

  // For sudo access - track which user we're viewing
  currentUserUuid: string | null = null;

  // Expandable row state
  expandedPayments: Set<string> = new Set();
  paymentRequests: Map<string, PaymentRequestsResponse> = new Map();
  paymentRequestsLoading: Map<string, boolean> = new Map();

  // Error state
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Computed getters
  get allPayments(): AdminPayment[] {
    return Array.from(this.payments.values()).sort((a, b) => {
      const dateA = a.date_created ? new Date(a.date_created).getTime() : 0;
      const dateB = b.date_created ? new Date(b.date_created).getTime() : 0;
      return dateB - dateA;
    });
  }

  // Actions
  clearError(): void {
    this.error = null;
  }

  /**
   * Fetch payments (own payments for assistants, or specific user for sudo)
   * @param refresh - If true, clears existing data and fetches from beginning
   * @param userUuid - If provided, fetches payments for this user (sudo only)
   */
  async fetchPayments(refresh = false, userUuid?: string): Promise<void> {
    if (this.paymentsLoading) return;
    if (!refresh && !this.paymentsHasMore) return;

    // If switching to a different user, treat as refresh
    if (userUuid && userUuid !== this.currentUserUuid) {
      refresh = true;
    }

    runInAction(() => {
      this.paymentsLoading = true;
      this.error = null;
      if (refresh) {
        this.payments.clear();
        this.paymentsCursor = null;
        this.paymentsHasMore = true;
        this.currentUserUuid = userUuid || null;
      }
    });

    try {
      const cursor = refresh ? undefined : this.paymentsCursor || undefined;
      const response = userUuid
        ? await adminPaymentApi.managerList(userUuid, cursor)
        : await adminPaymentApi.list(cursor);

      runInAction(() => {
        response.items.forEach((payment) => {
          this.payments.set(payment.key, payment);
        });
        this.paymentsCursor = response.cursor;
        this.paymentsHasMore = response.has_more;
        this.paymentsLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.paymentsLoading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch payments';
      });
    }
  }

  /**
   * Toggle expansion of a payment row. Fetches requests on first expand.
   */
  async togglePaymentExpanded(paymentKey: string): Promise<void> {
    if (this.expandedPayments.has(paymentKey)) {
      runInAction(() => {
        this.expandedPayments.delete(paymentKey);
      });
      return;
    }

    runInAction(() => {
      this.expandedPayments.add(paymentKey);
    });

    // Skip fetch if already cached
    if (this.paymentRequests.has(paymentKey)) return;

    runInAction(() => {
      this.paymentRequestsLoading.set(paymentKey, true);
    });

    try {
      const response = await adminPaymentApi.fetchRequests(paymentKey);
      runInAction(() => {
        this.paymentRequests.set(paymentKey, response);
        this.paymentRequestsLoading.delete(paymentKey);
      });
    } catch {
      runInAction(() => {
        this.paymentRequestsLoading.delete(paymentKey);
      });
    }
  }

  /**
   * Clear all payment data (useful when switching contexts)
   */
  clear(): void {
    this.payments.clear();
    this.paymentsCursor = null;
    this.paymentsHasMore = true;
    this.currentUserUuid = null;
    this.expandedPayments.clear();
    this.paymentRequests.clear();
    this.paymentRequestsLoading.clear();
    this.error = null;
  }
}

export const adminPaymentStore = new AdminPaymentStore();
